import { FieldArray, getIn } from 'formik';
import { UnitExtraPartsField } from '../units/UnitExtraPartsField';
import { last, sortBy, sumBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { useOnChange } from '../../../../helpers/hooks/useOnChange';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete.svg';
import { Alert } from '../../../../shared/Alert';
import { SecondaryButton } from '../../../../shared/Button';
import { Expandable } from '../../../../shared/Expandable';
import { FieldErrorIndicator } from '../../../../shared/form/FieldErrorIndicator';
import { FormContext } from '../../../../shared/form/Form';
import { FieldContainer } from '../../../../shared/form/FormField';
import { FormFieldRow } from '../../../../shared/form/FormFieldRow';
import { CheckboxField } from '../../../../shared/form/inputs/CheckboxField';
import { SelectOptions } from '../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../../shared/form/ReadOnlyField';
import { Header4 } from '../../../../shared/Headers';
import { colourNeutral09, colourNeutral10 } from '../../../../styling/colours';
import { spacing01, spacing02, spacing03, spacing04, spacing05 } from '../../../../styling/spacing';
import { ProjectContext } from '../../../projects/project-section/ProjectContext';
import { DeleteButton } from '../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../DeleteConfirmation';
import { HeatPumpUnitFormModel } from '../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import {
  groundCollectorTypeOptions,
  heatPumpTypeSelectOptions,
  swimmingPoolTypeOptions,
} from '../quoteOrDesignFormData';
import { QuoteOrDesignFormExpandControlChevron } from '../QuoteOrDesignFormExpandControlChevron';
import { heatPumpFieldName, heatPumpUnitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { SectionHeader } from '../SectionHeader';
import { CylinderVolumeField } from '../units/CylinderVolumeField';
import { UnitFieldContext } from '../units/UnitFieldContext';
import {
  calculateAdjustedHeatLossRateInWattsPerSquareMetre,
  calculateAnnualHeatingRequirementInKilowattHours,
  calculateBuildingHeatLoadInKilowatts,
  calculateGroundCollectorLengthInMetres,
  getOutputInKilowattsOfHeatPumpUnitForQuote,
  percentageCoverageOfHeatPumpUnits,
} from './heatPumpCalculations';
import { getRequiredHeatPumpUnitCount } from '../../heat-pump/heatPumpResponse';
import { canAddHeatPumpUnit, HeatPumpFormModel, padHeatPumpUnitPartsToRequiredCount } from './heatPumpFormModel';

type Props = {
  heatPump: HeatPumpFormModel;
  isExpanded: boolean;
  deleteHeatPump: () => void;
};

export const HeatPumpFields = (props: Props) => {
  const { heatPump, isExpanded, deleteHeatPump } = props;

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { formType } = formContext;
  const { values, setFieldValue, errors, touched } = formContext.formProps;

  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));
  const { unit, unitIndex, designCalculations, totalAreaInMetresSquared } = unitFieldContext;

  const heatLossInWatts =
    designCalculations != null &&
    !designCalculations.inProgress &&
    designCalculations.error == null &&
    designCalculations.response != null
      ? designCalculations.response.heatLossInWatts + designCalculations.response.downlossInWatts
      : null;

  const numberOfBedrooms = unit.numberOfBedrooms;

  const {
    heatPumpUnitOptions,
    microgenerationCertificationSchemeLocationOptions,
    newBuildSupportOptions,
    newBuildSupportOptionsMetadata,
    retrofitSupportOptions,
    retrofitSupportOptionsMetadata,
    airSourceDockingCodeOptions,
    groundSourceDockingCodeOptions,
    heatPumpPartCodesByDockingTypeCode,
  } = formContext.referenceData;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;
  const { project } = useContext(ProjectContext);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const buildType = project ? project.buildTypeCode : 'NewBuild';
  const supportOptions = buildType === 'NewBuild' ? newBuildSupportOptions : retrofitSupportOptions;
  const supportOptionsMetadata =
    buildType === 'NewBuild' ? newBuildSupportOptionsMetadata : retrofitSupportOptionsMetadata;

  const quantityOfUnitsOnQuote = sumBy(values.units, unitOnQuote =>
    isNaN(Number(unitOnQuote.quantity)) ? 0 : Number(unitOnQuote.quantity),
  );
  const unitHasSolarComponent = unit.solar !== null;

  const filteredSupportOptions = useMemo(() => {
    return supportOptions.filter(supportOption => {
      const supportOptionPartName = supportOption.text;
      const supportOptionMetadata = supportOptionsMetadata[supportOptionPartName.toLowerCase()];

      const optionSuitableForUnitQuantity =
        quantityOfUnitsOnQuote >= supportOptionMetadata.minimumUnitCountSuitableFor &&
        (supportOptionMetadata.maximumUnitCountSuitableFor === null ||
          quantityOfUnitsOnQuote <= supportOptionMetadata.maximumUnitCountSuitableFor);

      return (
        optionSuitableForUnitQuantity &&
        (quantityOfUnitsOnQuote > 1 || unitHasSolarComponent === supportOptionMetadata.suitableForSolar)
      );
    });
  }, [quantityOfUnitsOnQuote, unitHasSolarComponent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      filteredSupportOptions.length > 0 &&
      (heatPump.supportOptionPartId == null ||
        filteredSupportOptions.every(supportOption => supportOption.value !== heatPump.supportOptionPartId))
    ) {
      setFieldValue(heatPumpFieldName(unitIndex, 'supportOptionPartId'), filteredSupportOptions[0].value);
    }
  }, [heatPump.supportOptionPartId, filteredSupportOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const dockingCodeOptions =
    heatPump.heatPumpTypeCode === 'AirSource' ? airSourceDockingCodeOptions : groundSourceDockingCodeOptions;

  const adjustedHeatLossRate = useMemo(
    () =>
      calculateAdjustedHeatLossRateInWattsPerSquareMetre(
        heatPump.heatLossRateInWattsPerSquareMetre,
        heatPump.closestMicrogenerationCertificationSchemeLocation,
      ),
    [heatPump.heatLossRateInWattsPerSquareMetre, heatPump.closestMicrogenerationCertificationSchemeLocation],
  );

  const annualHeatingRequirementInKilowattHours = calculateAnnualHeatingRequirementInKilowattHours(
    heatPump,
    numberOfBedrooms,
  );

  useOnChange(() => {
    if (formType === 'Quote') {
      return;
    }

    if (heatLossInWatts != null && totalAreaInMetresSquared !== 0) {
      const roundedHeatLossRateInWattsPerSquareMetre = Math.round(heatLossInWatts / totalAreaInMetresSquared);
      setFieldValue(
        heatPumpFieldName(unitIndex, 'heatLossRateInWattsPerSquareMetre'),
        roundedHeatLossRateInWattsPerSquareMetre.toString(),
      );
    } else {
      setFieldValue(heatPumpFieldName(unitIndex, 'heatLossRateInWattsPerSquareMetre'), '');
    }
  }, [heatLossInWatts, totalAreaInMetresSquared]);

  useOnChange(() => {
    if (adjustedHeatLossRate && heatPump.areaInMetresSquared) {
      const buildingHeatLoadInKilowatts = calculateBuildingHeatLoadInKilowatts(
        adjustedHeatLossRate,
        heatPump.areaInMetresSquared,
        heatPump.swimmingPoolTypeCode,
        heatPump.swimmingPoolPowerInKilowatts,
      );
      if (buildingHeatLoadInKilowatts != null) {
        setFieldValue(heatPumpFieldName(unitIndex, 'buildingHeatLoadInKilowatts'), `${buildingHeatLoadInKilowatts}`);
      }
    }
  }, [
    adjustedHeatLossRate,
    heatPump.areaInMetresSquared,
    heatPump.swimmingPoolTypeCode,
    heatPump.swimmingPoolPowerInKilowatts,
  ]);

  const selectedDockingCode = unit.heatPump?.dockingCode;
  const selectedDockingCodeExists =
    selectedDockingCode && heatPumpPartCodesByDockingTypeCode[selectedDockingCode] != undefined;

  const requiredHeatPumpUnitCount = getRequiredHeatPumpUnitCount(
    selectedDockingCode ?? null,
    formContext.referenceData,
  );

  useOnChange(() => {
    setFieldValue(heatPumpFieldName(unitIndex, 'requiredHeatPumpUnitCount'), requiredHeatPumpUnitCount);

    const paddedHeatPumpUnitParts = padHeatPumpUnitPartsToRequiredCount(
      heatPump.heatPumpUnitParts,
      requiredHeatPumpUnitCount,
    );

    if (paddedHeatPumpUnitParts !== heatPump.heatPumpUnitParts) {
      setFieldValue(heatPumpFieldName(unitIndex, 'heatPumpUnitParts'), paddedHeatPumpUnitParts);
    }
  }, [requiredHeatPumpUnitCount]);

  const availableHeatPumpUnitOptions = useMemo(
    () =>
      heatPumpUnitOptions.filter(
        option =>
          option.value === null ||
          !selectedDockingCodeExists ||
          (heatPumpPartCodesByDockingTypeCode[selectedDockingCode].includes(option.value.displayName) &&
            (option.value.isAirSourceHeatPump || heatPump.heatPumpTypeCode !== 'AirSource') &&
            (!option.value.isAirSourceHeatPump || heatPump.heatPumpTypeCode === 'AirSource') &&
            (heatPump.heatPumpTypeCode !== 'AirSource' ||
              heatPump.isCoastalLocation === option.value.suitableForCoastalLocation)),
      ),
    [
      heatPump.heatPumpTypeCode,
      heatPump.isCoastalLocation,
      heatPumpUnitOptions,
      heatPumpPartCodesByDockingTypeCode,
      selectedDockingCode,
    ],
  );

  useOnChange(() => {
    const buildingHeatLoadInKilowatts = Number(heatPump.buildingHeatLoadInKilowatts);
    if (
      formType === 'Design' ||
      isNaN(buildingHeatLoadInKilowatts) ||
      heatPump.closestMicrogenerationCertificationSchemeLocation == null ||
      heatPump.heatPumpUnitParts.length > 1
    ) {
      return;
    }
    const potentialHeatPumpUnitOptions = availableHeatPumpUnitOptions.filter(
      option => option.value != null,
    ) as SelectOptions<HeatPumpUnitFormModel>;

    const sortedHeatPumpUnitOptions = sortBy(potentialHeatPumpUnitOptions, option =>
      getOutputInKilowattsOfHeatPumpUnitForQuote(
        option.value,
        heatPump.closestMicrogenerationCertificationSchemeLocation,
        buildType,
      ),
    );

    const bestHeatPumpUnitOption = sortedHeatPumpUnitOptions.find(option => {
      const outputInKilowatts = getOutputInKilowattsOfHeatPumpUnitForQuote(
        option.value,
        heatPump.closestMicrogenerationCertificationSchemeLocation,
        buildType,
      );
      return outputInKilowatts >= buildingHeatLoadInKilowatts;
    });

    if (bestHeatPumpUnitOption) {
      setFieldValue(
        heatPumpFieldName(unitIndex, 'heatPumpUnitParts'),
        padHeatPumpUnitPartsToRequiredCount([bestHeatPumpUnitOption.value], requiredHeatPumpUnitCount),
      );
    } else if (sortedHeatPumpUnitOptions.length > 0) {
      setFieldValue(
        heatPumpFieldName(unitIndex, 'heatPumpUnitParts'),
        padHeatPumpUnitPartsToRequiredCount(
          [assertNotNull(last(sortedHeatPumpUnitOptions)).value],
          requiredHeatPumpUnitCount,
        ),
      );
    }
  }, [
    buildType,
    heatPump.closestMicrogenerationCertificationSchemeLocation,
    heatPump.buildingHeatLoadInKilowatts,
    availableHeatPumpUnitOptions,
    requiredHeatPumpUnitCount,
  ]);

  useOnChange(() => {
    const percentageCoverage = percentageCoverageOfHeatPumpUnits(
      Number(heatPump.buildingHeatLoadInKilowatts),
      heatPump.heatPumpUnitParts,
      heatPump.closestMicrogenerationCertificationSchemeLocation,
      buildType,
    );
    setFieldValue(heatPumpFieldName(unitIndex, 'percentageCoverage'), percentageCoverage);
  }, [
    heatPump.buildingHeatLoadInKilowatts,
    heatPump.heatPumpUnitParts,
    heatPump.closestMicrogenerationCertificationSchemeLocation,
    buildType,
    setFieldValue,
    unitIndex,
  ]);

  useOnChange(() => {
    const nonNullHeatPumpUnitParts = heatPump.heatPumpUnitParts
      .filter(heatPumpUnitPart => heatPumpUnitPart != null)
      .map(assertNotNull);

    if (annualHeatingRequirementInKilowattHours != null) {
      const groundCollectorLengthInMetres = calculateGroundCollectorLengthInMetres(
        nonNullHeatPumpUnitParts,
        annualHeatingRequirementInKilowattHours,
      );
      setFieldValue(
        heatPumpFieldName(unitIndex, 'groundCollectorLengthInMetres'),
        groundCollectorLengthInMetres != null ? groundCollectorLengthInMetres.toString() : '',
      );
    }
  }, [
    heatPump.heatPumpUnitParts.map(heatPumpUnitPart => heatPumpUnitPart?.displayName).join(','),
    annualHeatingRequirementInKilowattHours,
  ]);

  const percentageCoverageError = getIn(errors, heatPumpFieldName(unitIndex, 'percentageCoverage'));
  const showPercentageCoverageError = percentageCoverageError != null && heatPump.percentageCoverage !== null;

  const heatLossRateInWattsPerSquareMetreError = getIn(
    errors,
    heatPumpFieldName(unitIndex, 'heatLossRateInWattsPerSquareMetre'),
  );
  const showHeatLossRateInWattsPerSquareMetreError = heatLossRateInWattsPerSquareMetreError != null;

  const heatPumpBaseFieldName = `units.${unitIndex}.heatPump`;
  const hasNestedError = getIn(errors, heatPumpBaseFieldName) && getIn(touched, heatPumpBaseFieldName);

  useOnChange(() => {
    if (formType === 'Design') {
      setFieldValue(heatPumpFieldName(unitIndex, 'areaInMetresSquared'), totalAreaInMetresSquared.toFixed(1));
    }
  }, [totalAreaInMetresSquared]);

  return (
    <Container>
      <SectionHeader>
        {hasNestedError && (
          <NestedErrorsIndicator
            fieldName={`units.${unitIndex}.heatPump`}
            showError={hasNestedError}
            error="Some fields are invalid"
          />
        )}
        <Header4>Heat Pump</Header4>
        <QuoteOrDesignFormExpandControlChevron
          onClick={() => {
            setFieldValue(heatPumpFieldName(unitIndex, 'isExpanded'), !isExpanded);
          }}
          isExpanded={isExpanded}
        />
        {!isInReadOnlyMode && (
          <DeleteButton onClick={() => setIsConfirmingDelete(true)} testId={heatPumpDeleteButtonTestId(unitIndex)} />
        )}
      </SectionHeader>
      <DeleteConfirmation
        isConfirmingDelete={isConfirmingDelete}
        setIsConfirmingDelete={setIsConfirmingDelete}
        deleteItem={deleteHeatPump}
        deleteButtonTestId={heatPumpConfirmDeleteButtonTestId(unitIndex)}
      />
      <Expandable isExpanded={isExpanded}>
        <FormFieldRow type="fill">
          <SelectField
            name={heatPumpFieldName(unitIndex, 'heatPumpTypeCode')}
            label="Heat Pump Type"
            options={heatPumpTypeSelectOptions}
          />
          <SelectField
            name={heatPumpFieldName(unitIndex, 'closestMicrogenerationCertificationSchemeLocation')}
            label="Closest MCS location"
            options={microgenerationCertificationSchemeLocationOptions}
            mapValueToKey={option => (option == null ? 'null' : option.name)}
          />
          {formType === 'Quote' ? (
            <>
              <TextInputField
                name={heatPumpFieldName(unitIndex, 'areaInMetresSquared')}
                label="Total floor area (m²)"
              />
              <TextInputField
                name={heatPumpFieldName(unitIndex, 'heatLossRateInWattsPerSquareMetre')}
                label={
                  <>
                    W/m<sup>2</sup>
                  </>
                }
              />
            </>
          ) : (
            <>
              <ReadOnlyField
                name={heatPumpFieldName(unitIndex, 'areaInMetresSquared')}
                label="Total floor area (m²)"
                value={totalAreaInMetresSquared == null ? '' : Math.round(totalAreaInMetresSquared).toString()}
              />
              <ReadOnlyField
                name={heatPumpFieldName(unitIndex, 'heatLossRateInWattsPerSquareMetre')}
                label={
                  <>
                    W/m<sup>2</sup>
                  </>
                }
                value={heatPump.heatLossRateInWattsPerSquareMetre}
                error={heatLossRateInWattsPerSquareMetreError}
                showError={showHeatLossRateInWattsPerSquareMetreError}
              />
            </>
          )}
        </FormFieldRow>
        <FormFieldRow type="fill">
          <ReadOnlyField
            name="adjustedWattsPerSquareMetre"
            label={
              <>
                Adjusted W/m<sup>2</sup>
              </>
            }
            value={adjustedHeatLossRate ? adjustedHeatLossRate.toString() : ''}
          />
          <TextInputField
            name={heatPumpFieldName(unitIndex, 'buildingHeatLoadInKilowatts')}
            label={<>Building heat load (kW)</>}
          />
          <ReadOnlyField
            name="buildingHeatingRequirement"
            label={<>Building heating requirement (kWh)</>}
            value={
              annualHeatingRequirementInKilowattHours != null ? annualHeatingRequirementInKilowattHours.toString() : ''
            }
          />
          <ReadOnlyField
            name={heatPumpFieldName(unitIndex, 'percentageCoverage')}
            label="Percentage Coverage"
            value={heatPump.percentageCoverage != null ? `${heatPump.percentageCoverage}%` : ''}
            error={percentageCoverageError}
            showError={showPercentageCoverageError}
          />
        </FormFieldRow>
        {heatPump.percentageCoverage != null && heatPump.percentageCoverage < 100 && (
          <OutputInsufficientAlert type="warning" body="Output is insufficient for the building heat load" />
        )}
        <FormFieldRow type="fill">
          <SelectField
            name={heatPumpFieldName(unitIndex, 'swimmingPoolTypeCode')}
            label="Swimming pool"
            options={swimmingPoolTypeOptions}
          />
          {heatPump.swimmingPoolTypeCode !== 'None' && (
            <>
              <TextInputField
                name={heatPumpFieldName(unitIndex, 'swimmingPoolEnergyConsumptionInKilowattHours')}
                label="Swimming pool energy (kWh)"
              />
              <TextInputField
                name={heatPumpFieldName(unitIndex, 'swimmingPoolPowerInKilowatts')}
                label="Swimming pool power (kW)"
              />
            </>
          )}
          <CylinderVolumeField />
        </FormFieldRow>
        {heatPump.heatPumpTypeCode === 'AirSource' && (
          <CheckboxesRow>
            <CheckboxField name={heatPumpFieldName(unitIndex, 'isCoastalLocation')} label="Coastal location" />
            <CheckboxField
              name={heatPumpFieldName(unitIndex, 'shouldAddUserHeatEmitter')}
              label="Add user heat emitter"
            />
            <CheckboxField
              name={heatPumpFieldName(unitIndex, 'hasPrivateWaterSupply')}
              label="Add private water supply"
            />
          </CheckboxesRow>
        )}

        <FormFieldRow type="fill">
          <SelectField
            name={heatPumpFieldName(unitIndex, 'dockingCode')}
            label="Docking Code"
            options={dockingCodeOptions}
          />
          <SelectField
            name={heatPumpFieldName(unitIndex, 'supportOptionPartId')}
            label="Support option"
            options={filteredSupportOptions}
          />
          {heatPump.heatPumpTypeCode === 'GroundSourceWithGroundLoops' && (
            <>
              <SelectField
                name={heatPumpFieldName(unitIndex, 'groundCollectorTypeCode')}
                label="Ground collector type"
                options={groundCollectorTypeOptions}
              />
              <TextInputField
                name={heatPumpFieldName(unitIndex, 'groundCollectorLengthInMetres')}
                label={<>Total length of ground collector (m)</>}
              />
            </>
          )}
        </FormFieldRow>

        <FieldArray name={heatPumpFieldName(unitIndex, 'heatPumpUnitParts')}>
          {arrayHelpers => (
            <MultiplePumpUnitsContainer>
              {heatPump.heatPumpUnitParts.map((_, index) => (
                <PumpUnitSelectContainer key={index}>
                  <SelectField
                    name={heatPumpUnitFieldName(unitIndex, index)}
                    label={index === 0 ? 'Heat pump unit' : null}
                    options={availableHeatPumpUnitOptions}
                    mapValueToKey={(value: HeatPumpUnitFormModel | null) => `${value != null ? value.partId : 'null'}`}
                  />
                  {index > 0 && !isInReadOnlyMode && (
                    <DeletePumpUnitButton onClick={() => arrayHelpers.remove(index)} title="Delete heat pump unit">
                      <DeleteIcon title="Delete" />
                    </DeletePumpUnitButton>
                  )}
                </PumpUnitSelectContainer>
              ))}
              {!isInReadOnlyMode && canAddHeatPumpUnit(heatPump.heatPumpUnitParts, requiredHeatPumpUnitCount) && (
                <SecondaryButton
                  onClick={() => {
                    arrayHelpers.push(null);
                  }}
                >
                  + Add Heat Pump Unit
                </SecondaryButton>
              )}
            </MultiplePumpUnitsContainer>
          )}
        </FieldArray>
        <UnitExtraPartsField unitIndex={unitIndex} technology="heatPump" />
      </Expandable>
    </Container>
  );
};

export const heatPumpDeleteButtonTestId = (unitIndex: number) => `unit-${unitIndex}-heat-pump-delete-button`;

export const heatPumpConfirmDeleteButtonTestId = (unitIndex: number) =>
  `unit-${unitIndex}-heat-pump-confirm-delete-button`;

const NestedErrorsIndicator = styled(FieldErrorIndicator)`
  margin-right: ${spacing03};
`;

const Container = styled.div`
  background: ${colourNeutral10};
  margin-top: ${spacing05};
  border: solid 2px ${colourNeutral09};
  padding: ${spacing04};
  position: relative;
`;

const MultiplePumpUnitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
`;

const PumpUnitSelectContainer = styled.div`
  margin-bottom: ${spacing02};
  display: flex;
  align-items: center;

  ${FieldContainer} {
    width: 100%;
    margin: 0;
  }
`;

const DeletePumpUnitButton = styled(SecondaryButton)`
  width: 40px;
  height: 40px;
  padding: 0 ${spacing01};
  margin-left: ${spacing02};
  align-self: flex-end;
`;

const CheckboxesRow = styled.div`
  padding: ${spacing02} 0 ${spacing05} 0;
  display: flex;

  ${FieldContainer} {
    margin-bottom: 0;
    margin-right: ${spacing03};
  }
`;

const OutputInsufficientAlert = styled(Alert)`
  margin: ${spacing04} 0 ${spacing05} 0;
`;
